import React, { useContext } from 'react';
import DashboardContext from '../../context/dashboardContext';
import Flex from '../../../../components/Flex';
import { makePrioStyles } from '../../../../theme/utils';
import { useTranslation } from 'react-i18next';
import { Button, useTheme } from '@prio365/prio365-react-library';
import { PrioTheme } from '../../../../theme/types';
import classNames from 'classnames';
import { parsePowerBIData } from '../util';

const useStyles = makePrioStyles((theme) => ({
  root: {
    height: '100%',
    overflowX: 'auto',
  },
  header: {
    fontSize: theme.font.fontSize.large,
    fontWeight: theme.font.fontWeight.bold,
  },
  noPowerBI: {
    color: theme.colors.application.typography.muted,
    fontSize: theme.font.fontSize.small,
    textAlign: 'center',
  },
}));

export interface PowerBIData {
  title: string;
  link: string;
}

interface DashboardLinksItemProps {
  className?: string;
  dashboardItemId?: string;
}

export const DashboardLinksItem: React.FC<DashboardLinksItemProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const { className, dashboardItemId } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const {
    initDashboardItems,
    expandDashboardItem,
    closeExpandedDashboardItem,
    expandedDashboardItem,
  } = useContext(DashboardContext);

  const dashboardItem = initDashboardItems.find(
    (item) =>
      item.type === 'powerBI' && item.dashboardItemId === dashboardItemId
  );

  const isExpanded =
    dashboardItem?.type === expandedDashboardItem?.type &&
    dashboardItem?.dashboardItemId === expandedDashboardItem?.dashboardItemId;

  const jsonContent = dashboardItem?.data;
  const powerBIData: PowerBIData = parsePowerBIData<PowerBIData>(jsonContent);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleResizeClick = () => {
    if (isExpanded) {
      return closeExpandedDashboardItem();
    }
    return expandDashboardItem(dashboardItem);
  };
  //#endregion

  return (
    <Flex.Column
      className={classNames(classes.root, className)}
      childrenGap={24}
    >
      <Flex.Row
        alignItems="center"
        justifyContent="space-between"
        childrenGap={theme.spacing.small}
      >
        <div className={classes.header} style={{ margin: 0 }}>
          {powerBIData?.title || t('dashboard:powerBI.defaultTitle')}
        </div>
        <Button
          type="link"
          iconProp={['fal', isExpanded ? 'xmark' : 'expand-wide']}
          iconColor={theme.colors.application.typography.muted}
          onClick={handleResizeClick}
          tooltip={isExpanded ? null : t('dashboard:widgets.expand')}
        />
      </Flex.Row>
      {powerBIData ? (
        <iframe
          src={powerBIData?.link}
          title={powerBIData?.title}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
        />
      ) : (
        <div className={classes.noPowerBI}>
          {t('dashboard:powerBI.noPowerBI')}
        </div>
      )}
    </Flex.Column>
  );
};

export default DashboardLinksItem;
